import Immutable from 'immutable';
import React from 'react';

import MessageDialog from 'sharedreact/components/MessageDialog';
import type ErrorData from 'sharedreact/data/ErrorData';
import LanguageSupport, { translate } from 'sharedreact/utils/LanguageSupport';

type Props = {
  language: string,
  errors: Immutable.List<ErrorData>,
  warnings: Immutable.List<ErrorData>,

  onClearError: () => void,
  onClearWarning: () => void
};

type State = {
  canShowDialog: boolean
};

const ERROR_DIALOG_Z_INDEX = 2700; // bigger then error toasts zIndex from WCR viewer, to make sure our error messages are always on top

class ErrorDialog extends React.Component<Props, State> {
  constructor (props: Props) {
    super(props);

    this.state = {
      canShowDialog: true
    };

    this.handleClose = this.handleClose.bind(this);
    this.handleClosed = this.handleClosed.bind(this);
  }

  handleClose () {
    this.setState({
      canShowDialog: false
    });
  }

  handleClosed () {
    const { errors, warnings, onClearError, onClearWarning } = this.props;

    if (errors.size !== 0) {
      onClearError();
    } else if (warnings.size !== 0) {
      onClearWarning();
    }

    this.setState({
      canShowDialog: true
    });
  }

  override render (): React.ReactNode {
    const {
      language, // eslint-disable-line no-unused-vars
      errors, warnings
    } = this.props;

    if (errors.size !== 0) {
      const error: ErrorData = errors.first();

      const title: string = (
        error.title != null
        ? LanguageSupport.tryToTranslate(error.title)
        : translate('shared.general.error')
      );

      const message: string = (
        error.message != null && error.message !== ''
        ? LanguageSupport.tryToTranslate(error.message, ...error.messageParams.toArray())
        : error.details
      );
      const info: string | undefined = (
        error.info != null
        ? LanguageSupport.tryToTranslate(error.info, ...error.infoParams.toArray())
        : undefined
      );

      return (
        <MessageDialog
          title={title}
          language={language}
          message={message}
          info={info}
          show={this.state.canShowDialog}
          zIndex={ERROR_DIALOG_Z_INDEX}
          onClose={this.handleClose}
          onClosed={this.handleClosed}
        />
      );
    } else if (warnings.size !== 0) {
      const warning: ErrorData = warnings.first();

      const title: string = (
        warning.title != null
        ? LanguageSupport.tryToTranslate(warning.title)
        : translate('shared.general.warning')
      );

      const message: string = (
        warning.message != null
        ? LanguageSupport.tryToTranslate(warning.message, ...warning.messageParams.toArray())
        : warning.details
      );

      const info: string | undefined = (
        warning.info != null
        ? LanguageSupport.tryToTranslate(warning.info, ...warning.infoParams.toArray())
        : undefined
      );

      return (
        <MessageDialog
          title={title}
          message={message}
          language={language}
          info={info}
          show={this.state.canShowDialog}
          zIndex={ERROR_DIALOG_Z_INDEX}
          onClose={this.handleClose}
          onClosed={this.handleClosed}
        />
      );
    } else {
      return (
        <MessageDialog
          title={translate('shared.general.error')}
          language={language}
          show={false}
          zIndex={ERROR_DIALOG_Z_INDEX}
          onClose={this.handleClose}
          onClosed={this.handleClosed}
        />
      );
    }
  }
}

export default ErrorDialog;
